import {
  addMethod,
  AnyObject,
  Flags,
  Maybe,
  mixed,
  MixedSchema,
  Schema,
  ValidationError,
} from 'yup';

import { isEmptyNullOrUndefined } from '@/utility/isEmptyNullOrUndefined';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type AnyPresentValue = {};

declare module 'yup' {
  interface MixedSchema<
    TType extends Maybe<AnyPresentValue> = AnyPresentValue | undefined,
    TContext = AnyObject,
    TDefault = undefined,
    TFlags extends Flags = '',
  > extends Schema<TType, TContext, TDefault, TFlags> {
    forbidden(): this;
  }
}

/**
 * Transforms a Yup ValidationError into an object where the keys are the paths
 * of the validation errors, and the values are the corresponding ValidationError objects.
 *
 * @param {ValidationError} [error] - The validation error object provided by Yup.
 * @returns {Record<string, ValidationError>} An object mapping each error path to its respective ValidationError.
 */
export const validationErrorToObject = (
  error?: ValidationError,
): Record<string, ValidationError> => {
  if (!error) {
    return {};
  } else if (error.inner) {
    const { inner: errors } = error;

    return errors.reduce(
      (
        mapped: Record<string, ValidationError>,
        current: ValidationError,
      ): Record<string, ValidationError> => {
        if (!current.path) {
          return mapped;
        }

        return { ...mapped, [current.path]: current };
      },
      {},
    );
  } else {
    return { [String(error.path)]: error };
  }
};

export const validationErrorsToString = (errors: Record<string, ValidationError>): string => {
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(errors).map(
        ([key, error]: [string, ValidationError]): [string, string] => [key, error.message],
        [],
      ),
    ),
  );
};

addMethod(mixed, 'forbidden', function (): MixedSchema {
  return this.test('forbidden-field', 'Field is not allowed', (value: any): boolean => {
    return isEmptyNullOrUndefined(value);
  });
});
